import React, { useEffect, useMemo, useState } from 'react';
import { Menu, Button } from 'antd';
import { observer } from 'mobx-react';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import classNames from 'classnames';

import { Icon } from '@/components';
import { useTabNavigate } from '@/hooks/useTabNavigate';

import type { MenuDataItem } from '@/const/route.config';
import type { MenuProps } from 'antd';

import './index.less';

type MenuItem = Required<MenuProps>['items'][number];

const getItem = (options: {
  label: React.ReactNode;
  key: string;
  icon?: string;
  children?: MenuItem[];
  type?: 'group' | 'divider';
}): MenuItem => {
  const { label, key, icon, children, type } = options;
  return {
    key,
    children,
    label,
    type,
    icon: icon ? <Icon type={icon} style={{ fontSize: 16 }} /> : undefined,
  } as any;
};

interface SubMenuProps {
  menuData: MenuDataItem[];
  flatMenus: Record<string, MenuDataItem> | null;
  currentMenu?: MenuDataItem;
}

const SiderMenu: React.FC<SubMenuProps> = ({ menuData, flatMenus, currentMenu }) => {
  const navigator = useTabNavigate();
  const [collapsed, setCollapsed] = useState<boolean>(false);

  const { layout_keys: matchMenuKeys, pureMenu = true } = currentMenu || {};

  const [openKeys, setOpenKeys] = useState<string[]>([]);

  useEffect(() => {
    if (matchMenuKeys?.length) {
      setOpenKeys(Array.from(new Set([...openKeys, ...matchMenuKeys])));
    }
  }, [matchMenuKeys]);

  const firstMenuItem = useMemo(() => {
    const [key] = matchMenuKeys || [];
    return menuData?.find((item) => item.path === key);
  }, [matchMenuKeys, menuData]);

  const subMenuData = useMemo(() => {
    const filterHiddenMenus = (menuItems: MenuDataItem[] = []): MenuDataItem[] => {
      return menuItems
        .filter(item => !item.hideInMenu)
        .map(item => {
          if (item.children && item.children.length > 0) {
            return {
              ...item,
              children: filterHiddenMenus(item.children)
            };
          }
          return item;
        });
    };
    return filterHiddenMenus(firstMenuItem?.children || []) || [];
  }, [firstMenuItem]);

  const { menuType } = JSON.parse(decodeURIComponent(firstMenuItem?.props || '{}'));

  const subMenuItems = subMenuData.reduce<MenuItem[]>((res, item) => {
    if (item.hideInMenu) {
      return res;
    }

    const { path = '', icon, name, type, children, hideChildrenInMenu } = item;
    const childreniItems =
      !hideChildrenInMenu &&
      children?.reduce<MenuItem[]>((res, route) => {
        if (!route.hideInMenu) {
          const { path = '', icon, name } = route;
          res.push(getItem({ label: name, key: path, icon }));
        }

        return res;
      }, []);

    res.push(
      getItem({
        label: name,
        key: path,
        icon,
        children: childreniItems && childreniItems?.length ? childreniItems : undefined,
        type,
      }),
    );

    return res;
  }, []);

  const siderWidth = useMemo(() => (collapsed ? 58 : 180), [collapsed]);

  if (!currentMenu || pureMenu || subMenuItems.length === 0) {
    return null;
  }

  const oncRowMenuBtnDom = (item: MenuDataItem) => {
    let dom: React.ReactNode = <React.Fragment />;

    switch (item.path) {
      case '/module/lgjMall/createEmptyShop':
        dom = (
          <img
            onClick={() => navigator('/module/lgjMall/createShop2')}
            src="https://oss.elebuys.com/202506181515080005105623.png"
            alt="创建商城"
          />
        );
        break;
      case '/module/lgjMall/application':
        dom = (
          <img
            onClick={() => navigator('/module/lgjMall/application')}
            src="https://oss.elebuys.com/202506181515080005105627.png"
            alt="管理商城"
          />
        );
        break;
      default:
        dom = (
          <Button block type="default" onClick={() => navigator(item.redirect || item.path)}>
            {item.name}
          </Button>
        );
        break;
    }

    return (
      <div className="fle-layout-sider-one-menu-btn" key={item.path}>
        {dom}
      </div>
    );
  };

  const oneRowMenuCellCom = (item: MenuDataItem) => (
    <div
      key={item.path}
      onClick={() => navigator(item.redirect || item.path)}
      className={classNames('fle-layout-sider-one-menu-cell', {
        active: matchMenuKeys?.includes(item.path),
      })}
    >
      {item.icon && <Icon type={item.icon} style={{ color: 'var(--primary-color)' }} />}
      {item.name}
    </div>
  );

  const oncRowMenuGroupDom = (item: MenuDataItem) => (
    <div key={item.path} className="fle-layout-sider-one-menu-group">
      <div className="fle-layout-sider-one-menu-group-name">{item.name}</div>
      {item.children?.map((route) => oneRowMenuCellCom(route))}
    </div>
  );

  return (
    <div
      className="fle-layout-sider"
      style={{ minWidth: currentMenu && !pureMenu ? siderWidth : 0 }}
    >
      {menuType === 'oneRowMenu' ? (
        <div className="fle-layout-sider-one-menu fle-scroll">
          <div className="fle-layout-sider-one-menu-list">
            {subMenuData?.map((item) => {
              if (item.hideInMenu) {
                return null;
              }

              const { menuType } = JSON.parse(decodeURIComponent(item?.props || '{}'));

              let dom: React.ReactNode = <React.Fragment />;
              switch (menuType) {
                case 'btn':
                  dom = oncRowMenuBtnDom(item);
                  break;
                case 'group':
                  dom = oncRowMenuGroupDom(item);
                  break;
                default:
                  dom = oneRowMenuCellCom(item);
                  break;
              }
              return dom;
            })}
          </div>
        </div>
      ) : (
        <div className="fle-menu">
          <div className="fle-menu-space" style={{ width: siderWidth }} />
          <div className="fle-menu-wrap">
            <div
              className="menu-action-wrap"
              onClick={() => setCollapsed(!collapsed)}
              style={{ left: collapsed ? 45 : 168 }}
            >
              {collapsed ? (
                <RightOutlined className="menu-action-close-icon" />
              ) : (
                <LeftOutlined className="menu-action-open-icon" />
              )}
            </div>

            <div className="fle-menu-sider fle-scroll" style={{ width: siderWidth }}>
              <Menu
                mode="inline"
                theme="light"
                inlineIndent={16}
                items={subMenuItems}
                inlineCollapsed={collapsed}
                selectedKeys={matchMenuKeys}
                openKeys={collapsed ? undefined : openKeys}
                onOpenChange={(keys) => setOpenKeys(keys)}
                onClick={({ key }) => {
                  const currentMenu = flatMenus?.[key];
                  const url = currentMenu?.redirect || currentMenu?.path || key;
                  if (currentMenu?.target === '_blank') {
                    window.open(url);
                  } else {
                    navigator(url);
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default observer(SiderMenu);
